import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { RouteComponentProps } from 'react-router-dom';
import { Button, Paper, Typography, Modal } from '@material-ui/core';
import { CheckCircleOutline } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useMst } from '../../core/stores';
import {
  getInformationFromValues,
  getProductPricing,
  ProductPricingMap,
} from '../../components/Product/pricingUtils';
import { OrderSummaryReadout } from '../../components/OrderSummaryReadout/OrderSummaryReadout';
import { getProductSchemaAndDetails } from '../../components/OrderSummaryReadout/utils';

export interface DOSummaryProps extends RouteComponentProps<{ id: string }> {}

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '60%',
    margin: 'auto',
    '& > *': {
      margin: theme.spacing(2),
    },
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(3),
    },
  },
  buttomButtonRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  modal: {
    display: 'flex',
    flexDirection: 'column',
    width: '50%',
    padding: theme.spacing(4),
    position: 'absolute',
    left: '50%',
    top: '30%',
    transform: 'translate(-50%, -50%)',
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  check: {
    height: '75px',
    fontSize: '75px',
    margin: '20px',
    color: theme.palette.success.main,
  },
}));

export const OrderSummaryForm: React.FC<DOSummaryProps> = observer(({ match }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const orderId = match.params.id;
  const { digitalOrdersStore, navigationStore } = useMst();
  const currentOrder: any = digitalOrdersStore.order(orderId);
  const facilityNames = currentOrder.facility.facilities.map(f =>
    f.facilityId ? digitalOrdersStore.facilityMap.get(f.facilityId).name : f.name,
  );
  const facilityExistence = currentOrder.facility.facilities.map(f => !!f.facilityId);
  const selectedProducts = currentOrder.products.productSelection;
  const [modalOpen, setModalOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const isOrderSubmitted = digitalOrdersStore.order(orderId)['submitted'] ? true : false;

  const navigateToHomePage = () => {
    navigationStore.push('/digital-orders');
  };

  const productPricing: ProductPricingMap = {};
  (Object.keys(selectedProducts) || []).forEach(productString => {
    const { details } = getProductSchemaAndDetails(currentOrder, productString, {
      facilityNames,
      facilityExistence,
    });
    const { product, years } = getInformationFromValues(productString, details);
    const facilityCount = currentOrder.facility.facilities.length;
    const pricing = getProductPricing({
      productName: productString,
      product,
      years,
      facilityCount,
      details,
    });
    productPricing[productString] = { pricing };
  });

  const submit = () => {
    setDisabled(true);
    setSubmitError(false);
    return digitalOrdersStore
      .finalizeAndSubmitOrder(orderId, productPricing)
      .then(() => {
        setDisabled(false);
        setModalOpen(true);
      })
      .catch(() => {
        setDisabled(false);
        setSubmitError(true);
      });
  };

  return (
    <>
      <Paper className={classes.container}>
        <OrderSummaryReadout order={currentOrder} orderId={orderId} />
        {submitError && (
          <Alert severity="error">
            There was a problem submitting your order. Please try again.
          </Alert>
        )}
        <div className={classes.buttomButtonRow}>
          <Button variant="contained" color="primary" onClick={submit} disabled={disabled}>
            Submit Order
          </Button>
        </div>
      </Paper>
      <Modal open={modalOpen || isOrderSubmitted}>
        <Paper className={classes.modal}>
          <Typography variant="h5">Order Submitted</Typography>
          <div className={classes.iconContainer}>
            <CheckCircleOutline className={classes.check} />
          </div>
          <Button variant="contained" color="primary" onClick={navigateToHomePage}>
            Back to home page
          </Button>
        </Paper>
      </Modal>
    </>
  );
});
